import {
  Button,
  Icon,
  InlineMessage,
  InputField,
  Typography,
} from "@saviynt/design-system";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { type PasswordPolicy } from "@repo/api";
import {
  usePasswordErrors,
  validatePasswordWithPolicy,
} from "../../../../hooks/usePasswordPolicy";
import { validatePasswordMatch } from "../../validation";

interface ResetPasswordStepProps {
  onSubmit: (newPassword: string) => void;
  isLoading: boolean;
  error?: string;
  policy?: PasswordPolicy;
}

export default function ResetPasswordStep({
  onSubmit,
  isLoading,
  error,
  policy,
}: ResetPasswordStepProps) {
  const intl = useIntl();
  const [visiblePassword, setVisiblePassword] = useState(false);
  const [visibleConfirmPassword, setVisibleConfirmPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmPasswordCritical, setConfirmPasswordCritical] = useState(false);

  const passwordErrors = usePasswordErrors(newPassword, policy);
  const showPasswordErrors =
    newPassword.length > 0 && passwordErrors.length > 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (policy && validatePasswordWithPolicy(newPassword, policy).length > 0)
      return;
    const matchValidation = validatePasswordMatch(newPassword, confirmPassword);
    if (!matchValidation.valid) {
      setConfirmPasswordCritical(true);
      return;
    }
    onSubmit(newPassword);
  };

  return (
    <form onSubmit={handleSubmit} className="mt-8 space-y-6">
      <InputField
        id="newPassword"
        name="newPassword"
        label={
          <Typography kind="label" htmlFor="newPassword">
            <FormattedMessage id="Ev6SEF" defaultMessage="New Password" />
          </Typography>
        }
        type={visiblePassword ? "text" : "password"}
        value={newPassword}
        setValue={setNewPassword}
        placeholder={intl.formatMessage({
          id: "Rh9tcQ",
          defaultMessage: "Enter new password",
          description: "New password field placeholder",
        })}
        suffixButtonType="password"
        suffixIsToggle={visiblePassword}
        suffixOnClick={() => setVisiblePassword(!visiblePassword)}
        HelperText={
          showPasswordErrors && (
            <div className="flex gap-2 items-start p-3 rounded bg-[var(--color-background-warning-subtle)] text-[var(--color-foreground-warning-bold)]">
              <Icon
                kind="AlertWarningFilled"
                size="small"
                color="--color-foreground-warning-bold"
              />
              <div className="text-sm">
                <strong>
                  {intl.formatMessage({
                    id: "2ICPrl",
                    defaultMessage: "Password must include:",
                    description: "Password requirements heading",
                  })}
                </strong>
                {passwordErrors.map((err, index) => (
                  <div key={index}>{err}</div>
                ))}
              </div>
            </div>
          )
        }
      />
      <InputField
        id="confirmPassword"
        name="confirmPassword"
        label={
          <Typography kind="label" htmlFor="confirmPassword">
            <FormattedMessage id="vfG+nh" defaultMessage="Confirm Password" />
          </Typography>
        }
        type={visibleConfirmPassword ? "text" : "password"}
        value={confirmPassword}
        setValue={(value) => {
          setConfirmPasswordCritical(false);
          setConfirmPassword(value);
        }}
        placeholder={intl.formatMessage({
          id: "z/LSdo",
          defaultMessage: "Confirm new password",
          description: "Confirm password field placeholder",
        })}
        suffixButtonType="password"
        suffixIsToggle={visibleConfirmPassword}
        suffixOnClick={() => setVisibleConfirmPassword(!visibleConfirmPassword)}
        isCritical={confirmPasswordCritical}
        setIsCritical={setConfirmPasswordCritical}
        CriticalHelperText={
          <InlineMessage
            text={intl.formatMessage({
              id: "WIV+mr",
              defaultMessage: "Passwords do not match",
              description: "Password mismatch error message",
            })}
            colorTheme="critical"
            size="small"
            leftIcon={<Icon kind="AlertCriticalFilled" />}
          />
        }
      />
      {error && (
        <InlineMessage
          text={error}
          colorTheme="critical"
          kind="background"
          size="small"
          leftIcon={<Icon kind="AlertCriticalFilled" />}
        />
      )}
      <Button
        type="submit"
        size="large"
        className="w-full justify-center"
        isDisabled={isLoading}
      >
        <FormattedMessage id="xl27nc" defaultMessage="Reset Password" />
      </Button>
    </form>
  );
}
